/**
 * Flavor text for item copies: a line under the item card that says what the
 * Veil remembered with the object. Each affix is a feeling remembered with
 * it (see `ITEM_RARITY_NAMES`), so lines are matched to an item by its
 * affixes, the essence those affixes lean to, and optionally its rarity or
 * kind. Every matching line is a candidate and one is picked at random,
 * seeded by the item copy's id and affixes: the same item always shows the
 * same line, and rerolling an affix can change it.
 *
 * Add lines freely; `ITEM_FLAVOR` is the whole catalog. A named item
 * (`named-items.ts`) always shows its own line instead.
 */

import type { CharacterEquipmentType, ItemInstance, ItemRarity } from './types/game';
import type { EssenceType } from './types/essence';
import { findItemAffix } from './item-affixes';
import { findNamedItem } from './named-items';
import { SeededRng } from './seeded-rng';

export interface ItemFlavorEntry {
    readonly id: string;
    readonly text: string;
    /** Matches an item with any of these affixes. */
    readonly affixIds?: readonly string[];
    /** Matches an item with any affix leaning to one of these essences. */
    readonly essences?: readonly EssenceType[];
    /** Only these rarities (all of them when omitted). */
    readonly rarities?: readonly ItemRarity[];
    /** Only these kinds of equipment (all of them when omitted). */
    readonly equipmentTypes?: readonly CharacterEquipmentType[];
}

/**
 * The catalog. An entry needs `affixIds` or `essences` to match an item with
 * affixes; one with neither only matches by rarity and kind (used for Hollow
 * items, which have no affixes).
 */
export const ITEM_FLAVOR: readonly ItemFlavorEntry[] = [
    // Hollow: the bare shape, no feeling.
    { id: 'hollow-weight', text: 'The Veil remembered its weight and nothing else. Whoever carried it left no trace of themselves.', rarities: ['common'] },
    { id: 'hollow-shape', text: 'A shape the Veil kept out of habit. It has never mattered to anyone.', rarities: ['common'] },
    { id: 'hollow-plain', text: 'Plain, and honest about it.', rarities: ['common'] },
    { id: 'hollow-blade', text: 'An edge with no story behind it, yet.', rarities: ['common'], equipmentTypes: ['melee'] },
    { id: 'hollow-guard', text: 'It has stopped blows before. None of them were remembered.', rarities: ['common'], equipmentTypes: ['shield', 'armor'] },

    // Stat affixes.
    { id: 'keen-anger', text: 'The grip still holds the shape of a clenched fist.', affixIds: ['keen'] },
    { id: 'keen-first', text: 'It remembers striking first, and liking it.', affixIds: ['keen'] },
    { id: 'marksman-eye', text: 'It remembers a steady breath and a long, quiet look.', affixIds: ['marksman'] },
    { id: 'steady-sure', text: 'Certainty clings to it. It knows where it means to land.', affixIds: ['steady'] },
    { id: 'steady-practiced', text: 'The same motion, ten thousand times, until the Veil could not forget it.', affixIds: ['steady'] },
    { id: 'brutal-warm', text: 'The head still feels warm from a blow struck in anger.', affixIds: ['brutal'] },
    { id: 'brutal-last', text: 'It ended something once. The Veil kept that.', affixIds: ['brutal'] },
    { id: 'swift-hurry', text: 'It remembers a hand that never waited for the other to finish.', affixIds: ['swift'] },
    { id: 'evasive-step', text: 'Whoever wore it was never quite where the blow arrived.', affixIds: ['evasive'] },
    { id: 'evasive-moves', text: 'It still moves the way its last wearer remembered moving.', affixIds: ['evasive'] },
    { id: 'fleet-road', text: 'It remembers a long road taken at a run.', affixIds: ['fleet'] },
    { id: 'warded-lie', text: 'Someone held this up against a lie and never lowered it.', affixIds: ['warded'] },
    { id: 'warded-truth', text: 'Truth settled into it like frost. Little else gets through.', affixIds: ['warded'] },
    { id: 'bulwark-stand', text: 'It remembers holding a line that should have broken.', affixIds: ['bulwark'] },
    { id: 'bulwark-door', text: 'It stood in a doorway once, and nothing passed.', affixIds: ['bulwark'] },
    { id: 'deep-well', text: 'Grief pooled in it and never drained away.', affixIds: ['deep'] },
    { id: 'deep-weight', text: 'Heavier than it looks. Most of the weight is sorrow.', affixIds: ['deep'] },
    { id: 'focused-hum', text: 'It hums faintly whenever essence moves nearby.', affixIds: ['focused'] },
    { id: 'flowing-tide', text: 'What you draw back comes to you a little fuller while you wear it, like a rising tide.', affixIds: ['flowing'] },
    { id: 'quickened-moment', text: 'It remembers a moment that passed too fast to hold.', affixIds: ['quickened'] },
    { id: 'practiced-lessons', text: 'It remembers every lesson its last owner learned the hard way.', affixIds: ['practiced'] },
    { id: 'practiced-hands', text: 'Worn smooth by hands that kept trying.', affixIds: ['practiced'] },

    // By the essence an affix leans to: these also cover each essence type's ability cost and cooldown affixes.
    { id: 'fury-ember', text: 'Fury still smolders in it, waiting for a reason.', essences: ['fury'] },
    { id: 'fury-teeth', text: 'It remembers clenched teeth and a held breath.', essences: ['fury'] },
    { id: 'courage-stand', text: 'Someone stood their ground with this. The Veil was watching.', essences: ['courage'] },
    { id: 'courage-steady', text: 'It is steadier than the hand that holds it.', essences: ['courage'] },
    { id: 'sorrow-mourning', text: 'It was carried to a grave and carried back alone.', essences: ['sorrow'] },
    { id: 'sorrow-quiet', text: 'There is a quiet in it, like a room after the guests have gone.', essences: ['sorrow'] },
    { id: 'memory-familiar', text: 'Holding it feels like remembering something you never lived.', essences: ['memory'] },
    { id: 'memory-names', text: 'It remembers names that nobody alive still says.', essences: ['memory'] },
    { id: 'truth-plain', text: 'It cannot be made to look like anything but what it is.', essences: ['truth'] },
    { id: 'truth-cold', text: 'Cold to the touch, the way the truth often is.', essences: ['truth'] },
    { id: 'resonance-chord', text: 'It answers the Veil the way a string answers a struck note.', essences: ['resonance'] },
    { id: 'resonance-many', text: 'Many small feelings, none strong, all still ringing.', essences: ['resonance'] },

    // Resonant: its memories are loud enough to drown out the wielder's own.
    { id: 'resonant-last-stand', text: 'Worn at a last stand the Veil refuses to forget. It still remembers holding.', rarities: ['epic'], essences: ['courage', 'truth', 'fury'] },
    { id: 'resonant-hand', text: 'Pried from a hand that never let go.', rarities: ['epic'], essences: ['fury', 'memory', 'sorrow'] },
    { id: 'resonant-echo', text: 'It hums with a memory that is not yours. Some days it is louder than your own.', rarities: ['epic'], essences: ['resonance', 'memory', 'truth', 'sorrow', 'courage', 'fury'] },
];

/** What an item needs for its flavor to be picked. */
export interface ItemFlavorSubject {
    /** The copy's id: seeds the pick, so a copy always shows the same line. */
    readonly id: string;
    readonly equipmentType?: CharacterEquipmentType;
    readonly instance?: ItemInstance;
}

/** Whether an entry fits an item with these affixes, rarity and kind. */
export function isItemFlavorMatch(entry: ItemFlavorEntry, subject: ItemFlavorSubject): boolean {
    const rarity = subject.instance?.rarity ?? 'common';
    if (entry.rarities && !entry.rarities.includes(rarity)) {
        return false;
    }
    if (entry.equipmentTypes && (subject.equipmentType === undefined || !entry.equipmentTypes.includes(subject.equipmentType))) {
        return false;
    }
    const affixIds = (subject.instance?.affixes ?? []).map((roll) => roll.affixId);
    const byAffix = entry.affixIds !== undefined;
    const byEssence = entry.essences !== undefined;
    if (!byAffix && !byEssence) {
        return true;
    }
    if (byAffix && entry.affixIds!.some((id) => affixIds.includes(id))) {
        return true;
    }
    return byEssence && affixIds.some((id) => {
        const essence = findItemAffix(id)?.essence;
        return essence !== undefined && entry.essences!.includes(essence);
    });
}

/** Every line that fits an item. */
export function listItemFlavorCandidates(subject: ItemFlavorSubject, catalog: readonly ItemFlavorEntry[] = ITEM_FLAVOR): ItemFlavorEntry[] {
    return catalog.filter((entry) => isItemFlavorMatch(entry, subject));
}

/**
 * The item's flavor line: one of its candidates, picked at random but seeded
 * by the copy's id and affixes. Undefined when nothing fits (a plain catalog copy
 * shown with no instance, say).
 */
export function resolveItemFlavor(subject: ItemFlavorSubject, catalog: readonly ItemFlavorEntry[] = ITEM_FLAVOR): ItemFlavorEntry | undefined {
    if (!subject.instance) {
        return undefined;
    }
    const named = findNamedItem(subject.instance.namedItemId);
    if (named) {
        return { id: named.id, text: named.flavor };
    }
    const candidates = listItemFlavorCandidates(subject, catalog);
    if (candidates.length === 0) {
        return undefined;
    }
    const affixKey = subject.instance.affixes.map((roll) => roll.affixId).join(',');
    const rng = new SeededRng(SeededRng.deriveSeed(subject.id, `flavor:${affixKey}`));
    return candidates[rng.randomInt(0, candidates.length)] ?? undefined;
}
